import {
  forwardRef,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';

/**
 * Input + FormField — Studio Linen v1.0
 *
 * `FormField` wraps an input/select/textarea with a label, helper, and
 * error message. Labels are auto-associated via `htmlFor`. Error state
 * adds a left brick bar + brick border.
 *
 *   <FormField label="Email" helper="We never share." error={err}>
 *     <Input type="email" name="email" required />
 *   </FormField>
 *
 * For Select / Textarea use the same FormField:
 *   <FormField label="Country">
 *     <Select>...</Select>
 *   </FormField>
 */

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  invalid?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, invalid = false, ...rest }, ref,
) {
  const cn = ['bq-input', invalid ? 'is-invalid' : '', className ?? ''].filter(Boolean).join(' ');
  return <input ref={ref} className={cn} aria-invalid={invalid || undefined} {...rest} />;
});

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { className, invalid = false, rows = 4, ...rest }, ref,
) {
  const cn = ['bq-input', 'bq-textarea', invalid ? 'is-invalid' : '', className ?? ''].filter(Boolean).join(' ');
  return <textarea ref={ref} rows={rows} className={cn} aria-invalid={invalid || undefined} {...rest} />;
});

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  invalid?: boolean;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { className, invalid = false, children, ...rest }, ref,
) {
  const cn = ['bq-input', 'bq-select', invalid ? 'is-invalid' : '', className ?? ''].filter(Boolean).join(' ');
  return (
    <div className="bq-select-wrap">
      <select ref={ref} className={cn} aria-invalid={invalid || undefined} {...rest}>
        {children}
      </select>
    </div>
  );
});

export interface FormFieldProps {
  label?: ReactNode;
  helper?: ReactNode;
  error?: ReactNode | null;
  required?: boolean;
  /** Optional id; auto-generated if omitted. Bound to first child input. */
  htmlFor?: string;
  className?: string;
  children: ReactNode;
}

/**
 * FormField composes label + control + helper.
 * Pass id via htmlFor or let it auto-generate; the inner input/select must
 * accept the id prop (we don't inject it because cloneElement is dirty —
 * caller passes id={fieldId} or wraps an <Input> at root).
 */
export function FormField({
  label, helper, error, required, htmlFor, className, children,
}: FormFieldProps) {
  const auto = useId();
  const id = htmlFor ?? auto;
  const helperId = `${id}-helper`;
  const errorId = `${id}-error`;
  const describedBy = [error ? errorId : null, helper ? helperId : null].filter(Boolean).join(' ');

  return (
    <div className={`bq-field ${error ? 'is-invalid' : ''} ${className ?? ''}`}>
      {label && (
        <label htmlFor={id} className="bq-field__label">
          {label}{required && <span className="bq-field__required" aria-hidden="true">*</span>}
        </label>
      )}
      <FieldInputWrap id={id} describedBy={describedBy || undefined}>
        {children}
      </FieldInputWrap>
      {error ? (
        <p id={errorId} className="bq-field__error" role="alert">{error}</p>
      ) : helper ? (
        <p id={helperId} className="bq-field__helper">{helper}</p>
      ) : null}
    </div>
  );
}

/**
 * Internal helper that just renders children — exists so future versions
 * can inject id/aria-describedby into children via context if desired.
 * For now the caller must wire id manually on the input, which is
 * explicit and avoids brittle cloneElement.
 */
function FieldInputWrap({ children }: { id: string; describedBy?: string; children: ReactNode }) {
  return <>{children}</>;
}
